import {
    ClientBillingBalanceSummaryEntity,
    ClientBillingOperationHistoryDailyRowEntity,
    ClientBillingOperationHistoryUsageEntity,
    ClientBillingOperationCostEntity,
    ClientBillingOperationUsageEntity,
    ClientBillingPackageEntity,
    ClientBillingPackageKindEntity,
    ClientBillingRobokassaCheckoutInputEntity,
    ClientBillingTestPaymentInputEntity,
    ClientBillingSummaryEntity,
    ClientPaymentRecordEntity,
} from '@/entities/client-billing.entity';
import {
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER,
    getClientBillingOperationHistoryUsageKey,
    getClientBillingTokenOperationLabel,
    type ClientBillingOperationHistoryUsageKey,
} from '@/shared/client-billing-operations';
import { toIsoDateInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import {
    DEFAULT_BILLING_CURRENCY,
    type BillingCurrency,
    normalizeBillingCurrency,
    toMoneyMajorUnits,
} from '@mrw/billing-money';

export const CLIENT_BILLING_PACKAGE_KINDS: readonly ClientBillingPackageKindEntity[] = [
    'ai_requests',
    'review_imports',
    'tokens',
];

const toObjectOrNull = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '');

export const isClientBillingPackageKind = (value: unknown): value is ClientBillingPackageKindEntity =>
    typeof value === 'string' && CLIENT_BILLING_PACKAGE_KINDS.includes(value as ClientBillingPackageKindEntity);

export const normalizeBillingNonNegativeInteger = (value: unknown): number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;

export const normalizeBillingNonNegativeNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }

    if (
        value &&
        typeof value === 'object' &&
        'toNumber' in value &&
        typeof (value as { toNumber: () => unknown }).toNumber === 'function'
    ) {
        const parsed = (value as { toNumber: () => unknown }).toNumber();
        return typeof parsed === 'number' && Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }

    return 0;
};

const normalizeBillingSignedNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    if (
        value &&
        typeof value === 'object' &&
        'toNumber' in value &&
        typeof (value as { toNumber: () => unknown }).toNumber === 'function'
    ) {
        const parsed = (value as { toNumber: () => unknown }).toNumber();
        return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
};

const normalizeNullableNonNegativeInteger = (value: unknown): number | null =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

const normalizeNullableNonNegativeNumber = (value: unknown): number | null => {
    const normalized = normalizeBillingNonNegativeNumber(value);
    return normalized > 0 || value === 0 || value === '0' ? normalized : null;
};

const normalizeStringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '') : [];

export const createEmptyClientBillingOperationHistoryUsage = (): ClientBillingOperationHistoryUsageEntity => ({
    reviewImport: 0,
    reviewAnalysis: 0,
    aiAnswerGeneration: 0,
    marketplaceAnswerSend: 0,
    templateGeneration: 0,
    wbReviewRead: 0,
    other: 0,
});

const addValidationError = (errors: Record<string, string[]>, field: string, message: string): void => {
    errors[field] = [...(errors[field] ?? []), message];
};

const isValidPaymentDate = (value: string): boolean => {
    const date = new Date(value);
    return value.trim().length > 0 && !Number.isNaN(date.getTime());
};

const isValidPaymentEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

type ClientBillingNormalizeOptions = {
    fallbackCurrency?: BillingCurrency;
};

export type ClientBillingOperationHistoryUsageInput = {
    day: string;
    operation: string;
    tokenAmount: unknown;
};

type ClientBillingTestPaymentDraft = Omit<ClientBillingTestPaymentInputEntity, 'clientId'>;
type ClientBillingRobokassaCheckoutDraft = Omit<ClientBillingRobokassaCheckoutInputEntity, 'clientId'>;

const getFallbackCurrency = (options?: ClientBillingNormalizeOptions): BillingCurrency =>
    options?.fallbackCurrency ?? DEFAULT_BILLING_CURRENCY;

const normalizeClientBillingCurrency = (value: unknown, options?: ClientBillingNormalizeOptions): BillingCurrency =>
    normalizeBillingCurrency(value) ?? getFallbackCurrency(options);

export const buildClientBillingBalanceSummary = ({
    purchasedTokens,
    usedTokens,
}: {
    purchasedTokens: unknown;
    usedTokens: unknown;
}): ClientBillingBalanceSummaryEntity => ({
    tokensRemaining: normalizeBillingNonNegativeNumber(purchasedTokens) - normalizeBillingNonNegativeNumber(usedTokens),
});

export const createDefaultClientBillingSummary = (
    currency: BillingCurrency = DEFAULT_BILLING_CURRENCY,
): ClientBillingSummaryEntity => ({
    currency,
    generatedAt: null,
    packages: [],
    payments: [],
    purchasedTotals: {
        tokens: 0,
        aiRequests: 0,
        reviewImports: 0,
    },
    paidTotals: {
        tokensPaidMinorUnits: 0,
        aiRequestsPaidMinorUnits: 0,
        reviewImportsPaidMinorUnits: 0,
    },
    usage: {
        tokens: 0,
        aiRequests: 0,
        reviewImports: 0,
        operations: [],
    },
    balance: {
        tokensRemaining: 0,
    },
    operationHistoryDaily: [],
    operationCosts: [],
});

export const normalizeClientBillingPackageEntity = (
    value: unknown,
    options?: ClientBillingNormalizeOptions,
): ClientBillingPackageEntity | null => {
    const source = toObjectOrNull(value);
    if (!source || !isClientBillingPackageKind(source.kind)) {
        return null;
    }

    return {
        id: toStringOrEmpty(source.id),
        kind: source.kind,
        name: toStringOrEmpty(source.name),
        quantity: normalizeBillingNonNegativeInteger(source.quantity),
        tokenQuantity: normalizeBillingNonNegativeNumber(source.tokenQuantity),
        priceMinorUnits: normalizeBillingNonNegativeInteger(source.priceMinorUnits),
        currency: normalizeClientBillingCurrency(source.currency, options),
        isActive: typeof source.isActive === 'boolean' ? source.isActive : false,
    };
};

export const normalizeClientBillingOperationCostEntity = (value: unknown): ClientBillingOperationCostEntity | null => {
    const source = toObjectOrNull(value);
    const operation = toStringOrEmpty(source?.operation);
    if (!source || !operation) {
        return null;
    }

    return {
        operation,
        displayName: toStringOrEmpty(source.displayName) || getClientBillingTokenOperationLabel(operation),
        tokenCost: normalizeBillingNonNegativeNumber(source.tokenCost),
        isActive: typeof source.isActive === 'boolean' ? source.isActive : false,
    };
};

export const normalizeClientBillingOperationUsageEntity = (
    value: unknown,
): ClientBillingOperationUsageEntity | null => {
    const source = toObjectOrNull(value);
    const operation = toStringOrEmpty(source?.operation);
    if (!source || !operation) {
        return null;
    }

    return {
        operation,
        displayName: toStringOrEmpty(source.displayName) || getClientBillingTokenOperationLabel(operation),
        unitQuantity: normalizeBillingNonNegativeNumber(source.unitQuantity),
        tokenAmount: normalizeBillingNonNegativeNumber(source.tokenAmount),
    };
};

export const normalizeClientPaymentRecordEntity = (
    value: unknown,
    options?: ClientBillingNormalizeOptions,
): ClientPaymentRecordEntity => {
    const source = toObjectOrNull(value);
    const packageKind = isClientBillingPackageKind(source?.packageKind) ? source.packageKind : null;

    return {
        id: toStringOrEmpty(source?.id),
        operationId: toStringOrEmpty(source?.operationId),
        paidAt: toStringOrEmpty(source?.paidAt),
        email: toStringOrEmpty(source?.email),
        amountMinorUnits: normalizeBillingNonNegativeInteger(source?.amountMinorUnits),
        currency: normalizeClientBillingCurrency(source?.currency, options),
        method: toStringOrEmpty(source?.method),
        status: toStringOrEmpty(source?.status),
        documents: normalizeStringArray(source?.documents),
        aiRequestsAmount: normalizeBillingNonNegativeInteger(source?.aiRequestsAmount),
        reviewImportsAmount: normalizeBillingNonNegativeInteger(source?.reviewImportsAmount),
        tokenAmount: normalizeBillingNonNegativeNumber(source?.tokenAmount),
        packageId: typeof source?.packageId === 'string' ? source.packageId : null,
        packageKind,
        packageQuantity: normalizeNullableNonNegativeInteger(source?.packageQuantity),
        packageName: typeof source?.packageName === 'string' ? source.packageName : null,
        packageTokenQuantity: normalizeNullableNonNegativeNumber(source?.packageTokenQuantity),
        packagePriceMinorUnits: normalizeNullableNonNegativeInteger(source?.packagePriceMinorUnits),
    };
};

const normalizeClientBillingOperationHistoryUsageEntity = (
    value: unknown,
): ClientBillingOperationHistoryUsageEntity => {
    const source = toObjectOrNull(value);
    const usage = createEmptyClientBillingOperationHistoryUsage();

    if (!source) {
        return usage;
    }

    for (const key of CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER) {
        usage[key] = normalizeBillingNonNegativeNumber(source[key]);
    }

    return usage;
};

export const normalizeClientBillingOperationHistoryDailyRowEntity = (
    value: unknown,
): ClientBillingOperationHistoryDailyRowEntity | null => {
    const source = toObjectOrNull(value);
    const day = toStringOrEmpty(source?.day);

    if (!source || !day) {
        return null;
    }

    return {
        day,
        balanceAtEndOfDay: normalizeBillingSignedNumber(source.balanceAtEndOfDay),
        totalTokenDelta: normalizeBillingSignedNumber(source.totalTokenDelta),
        topUpTokens: normalizeBillingNonNegativeNumber(source.topUpTokens),
        usage: normalizeClientBillingOperationHistoryUsageEntity(source.usage),
    };
};

export const normalizeClientBillingSummaryEntity = (
    value: unknown,
    options?: ClientBillingNormalizeOptions,
): ClientBillingSummaryEntity => {
    const fallbackCurrency = getFallbackCurrency(options);
    const defaults = createDefaultClientBillingSummary(fallbackCurrency);
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const summaryCurrency = normalizeClientBillingCurrency(source.currency, { fallbackCurrency });
    const nestedOptions = { fallbackCurrency: summaryCurrency };
    const purchasedTotals = toObjectOrNull(source.purchasedTotals);
    const paidTotals = toObjectOrNull(source.paidTotals);
    const usage = toObjectOrNull(source.usage);
    const normalizedPurchasedTotals = {
        tokens: normalizeBillingNonNegativeNumber(purchasedTotals?.tokens),
        aiRequests: normalizeBillingNonNegativeInteger(purchasedTotals?.aiRequests),
        reviewImports: normalizeBillingNonNegativeInteger(purchasedTotals?.reviewImports),
    };
    const normalizedUsage = {
        tokens: normalizeBillingNonNegativeNumber(usage?.tokens),
        aiRequests: normalizeBillingNonNegativeInteger(usage?.aiRequests),
        reviewImports: normalizeBillingNonNegativeInteger(usage?.reviewImports),
        operations: Array.isArray(usage?.operations)
            ? usage.operations
                  .map((operationUsage) => normalizeClientBillingOperationUsageEntity(operationUsage))
                  .filter(
                      (operationUsage): operationUsage is ClientBillingOperationUsageEntity => operationUsage !== null,
                  )
            : defaults.usage.operations,
    };

    return {
        currency: summaryCurrency,
        generatedAt: typeof source.generatedAt === 'string' ? source.generatedAt : null,
        packages: Array.isArray(source.packages)
            ? source.packages
                  .map((billingPackage) => normalizeClientBillingPackageEntity(billingPackage, nestedOptions))
                  .filter((billingPackage): billingPackage is ClientBillingPackageEntity => billingPackage !== null)
            : defaults.packages,
        operationCosts: Array.isArray(source.operationCosts)
            ? source.operationCosts
                  .map((operationCost) => normalizeClientBillingOperationCostEntity(operationCost))
                  .filter((operationCost): operationCost is ClientBillingOperationCostEntity => operationCost !== null)
            : defaults.operationCosts,
        payments: Array.isArray(source.payments)
            ? source.payments.map((payment) => normalizeClientPaymentRecordEntity(payment, nestedOptions))
            : defaults.payments,
        purchasedTotals: normalizedPurchasedTotals,
        paidTotals: {
            tokensPaidMinorUnits: normalizeBillingNonNegativeInteger(paidTotals?.tokensPaidMinorUnits),
            aiRequestsPaidMinorUnits: normalizeBillingNonNegativeInteger(paidTotals?.aiRequestsPaidMinorUnits),
            reviewImportsPaidMinorUnits: normalizeBillingNonNegativeInteger(paidTotals?.reviewImportsPaidMinorUnits),
        },
        usage: normalizedUsage,
        balance: buildClientBillingBalanceSummary({
            purchasedTokens: normalizedPurchasedTotals.tokens,
            usedTokens: normalizedUsage.tokens,
        }),
        operationHistoryDaily: Array.isArray(source.operationHistoryDaily)
            ? source.operationHistoryDaily
                  .map((historyRow) => normalizeClientBillingOperationHistoryDailyRowEntity(historyRow))
                  .filter(
                      (historyRow): historyRow is ClientBillingOperationHistoryDailyRowEntity => historyRow !== null,
                  )
            : defaults.operationHistoryDaily,
    };
};

export const validateClientBillingPackageCurrency = (
    selectedPackage: ClientBillingPackageEntity | null | undefined,
    billingCurrency: BillingCurrency,
): Record<string, string[]> => {
    if (!selectedPackage || selectedPackage.currency === billingCurrency) {
        return {};
    }

    return {
        packageId: ['Выбранный пакет недоступен.'],
    };
};

export const toBillingMoneyMajorUnits = (minorUnits: number): number => toMoneyMajorUnits(minorUnits);

export const getClientBillingPackageGrantedAmounts = (
    billingPackage: ClientBillingPackageEntity,
): Pick<ClientPaymentRecordEntity, 'aiRequestsAmount' | 'reviewImportsAmount' | 'tokenAmount'> => ({
    aiRequestsAmount: billingPackage.kind === 'ai_requests' ? billingPackage.quantity : 0,
    reviewImportsAmount: billingPackage.kind === 'review_imports' ? billingPackage.quantity : 0,
    tokenAmount:
        billingPackage.kind === 'tokens'
            ? billingPackage.tokenQuantity
            : billingPackage.kind === 'ai_requests'
              ? billingPackage.quantity
              : billingPackage.tokenQuantity,
});

export const getClientBillingUsedTokens = (summary: ClientBillingSummaryEntity): number =>
    normalizeBillingNonNegativeNumber(summary.usage.tokens);

export const getClientBillingRemainingTokens = (summary: ClientBillingSummaryEntity): number =>
    buildClientBillingBalanceSummary({
        purchasedTokens: summary.purchasedTotals.tokens,
        usedTokens: getClientBillingUsedTokens(summary),
    }).tokensRemaining;

export const getClientBillingAffordableTokens = (summary: ClientBillingSummaryEntity): number =>
    Math.max(0, getClientBillingRemainingTokens(summary));

export const getClientBillingUsedAiRequests = (
    summary: ClientBillingSummaryEntity,
    effectiveAiUsageAmount = 0,
): number =>
    Math.max(
        normalizeBillingNonNegativeInteger(summary.usage.aiRequests),
        normalizeBillingNonNegativeInteger(effectiveAiUsageAmount),
    );

export const getClientBillingRemainingAiRequests = (
    summary: ClientBillingSummaryEntity,
    effectiveAiUsageAmount = 0,
): number =>
    Math.max(
        0,
        normalizeBillingNonNegativeInteger(summary.purchasedTotals.aiRequests) -
            getClientBillingUsedAiRequests(summary, effectiveAiUsageAmount),
    );

export const createBillingOperationId = (): string => {
    const randomId =
        typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    return `test-payment-${randomId}`;
};

export const normalizeBillingOperationId = (value: unknown): string => {
    const operationId = typeof value === 'string' ? value.trim() : '';
    return operationId || createBillingOperationId();
};

export const validateClientBillingTestPaymentInput = (
    input: ClientBillingTestPaymentDraft,
    selectedPackage: ClientBillingPackageEntity | null | undefined,
): Record<string, string[]> => {
    const errors: Record<string, string[]> = {};
    const packageId = input.packageId.trim();

    if (!packageId) {
        addValidationError(errors, 'packageId', 'Пакет обязателен.');
    } else if (!selectedPackage || !selectedPackage.isActive || selectedPackage.id !== packageId) {
        addValidationError(errors, 'packageId', 'Выбранный пакет недоступен.');
    }

    if (!isValidPaymentDate(input.paidAt)) {
        addValidationError(errors, 'paidAt', 'Дата платежа должна быть корректной ISO-датой.');
    }

    if (!input.email.trim()) {
        addValidationError(errors, 'email', 'Email обязателен.');
    } else if (!isValidPaymentEmail(input.email)) {
        addValidationError(errors, 'email', 'Email должен быть корректным.');
    }

    if (!input.method.trim()) {
        addValidationError(errors, 'method', 'Метод обязателен.');
    }

    if (!input.status.trim()) {
        addValidationError(errors, 'status', 'Статус обязателен.');
    }

    if (!selectedPackage || !selectedPackage.isActive || selectedPackage.id !== packageId) {
        return errors;
    }

    const grantedAmounts = getClientBillingPackageGrantedAmounts(selectedPackage);

    if (input.amountMinorUnits !== selectedPackage.priceMinorUnits) {
        addValidationError(errors, 'amountMinorUnits', 'Сумма должна совпадать с ценой выбранного пакета.');
    }

    if (input.aiRequestsAmount !== grantedAmounts.aiRequestsAmount) {
        addValidationError(errors, 'aiRequestsAmount', 'Кол-во ИИ-запросов должно совпадать с выбранным пакетом.');
    }

    if (input.reviewImportsAmount !== grantedAmounts.reviewImportsAmount) {
        addValidationError(
            errors,
            'reviewImportsAmount',
            'Кол-во импортов отзывов должно совпадать с выбранным пакетом.',
        );
    }

    if (input.tokenAmount !== grantedAmounts.tokenAmount) {
        addValidationError(errors, 'tokenAmount', 'Кол-во токенов должно совпадать с выбранным пакетом.');
    }

    return errors;
};

export const validateClientBillingRobokassaCheckoutInput = (
    input: ClientBillingRobokassaCheckoutDraft,
    selectedPackage: ClientBillingPackageEntity | null | undefined,
): Record<string, string[]> => {
    const errors: Record<string, string[]> = {};
    const packageId = input.packageId.trim();

    if (!packageId) {
        addValidationError(errors, 'packageId', 'Пакет обязателен.');
    } else if (!selectedPackage || !selectedPackage.isActive || selectedPackage.id !== packageId) {
        addValidationError(errors, 'packageId', 'Выбранный пакет недоступен.');
    }

    if (!input.email.trim()) {
        addValidationError(errors, 'email', 'Email обязателен.');
    } else if (!isValidPaymentEmail(input.email)) {
        addValidationError(errors, 'email', 'Email должен быть корректным.');
    }

    return errors;
};

export const getFirstClientBillingValidationMessage = (errors: Record<string, string[]>): string | null => {
    for (const fieldErrors of Object.values(errors)) {
        const message = fieldErrors.find((fieldError) => fieldError.trim().length > 0);
        if (message) {
            return message;
        }
    }

    return null;
};

type ClientBillingOperationHistoryDailyDraft = {
    day: string;
    topUpTokens: number;
    usage: ClientBillingOperationHistoryUsageEntity;
};

const roundBillingTokenMath = (value: number): number => Number(value.toFixed(6));

const getOrCreateDailyHistoryDraft = (
    draftsByDay: Map<string, ClientBillingOperationHistoryDailyDraft>,
    day: string,
): ClientBillingOperationHistoryDailyDraft => {
    const existingDraft = draftsByDay.get(day);
    if (existingDraft) {
        return existingDraft;
    }

    const draft = {
        day,
        topUpTokens: 0,
        usage: createEmptyClientBillingOperationHistoryUsage(),
    };
    draftsByDay.set(day, draft);
    return draft;
};

const sumDailyHistoryUsage = (usage: ClientBillingOperationHistoryUsageEntity): number =>
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER.reduce((total, key) => total + usage[key], 0);

const getPaymentHistoryDay = (payment: ClientPaymentRecordEntity): string | null => toIsoDateInGmtPlus3(payment.paidAt);

export const buildClientBillingOperationHistoryDailyRows = ({
    payments,
    usageRows,
}: {
    payments: ClientPaymentRecordEntity[];
    usageRows: ClientBillingOperationHistoryUsageInput[];
}): ClientBillingOperationHistoryDailyRowEntity[] => {
    const draftsByDay = new Map<string, ClientBillingOperationHistoryDailyDraft>();

    for (const payment of payments) {
        const tokenAmount = normalizeBillingNonNegativeNumber(payment.tokenAmount);
        const day = getPaymentHistoryDay(payment);
        if (!day || tokenAmount <= 0) {
            continue;
        }

        const draft = getOrCreateDailyHistoryDraft(draftsByDay, day);
        draft.topUpTokens = roundBillingTokenMath(draft.topUpTokens + tokenAmount);
    }

    for (const usageRow of usageRows) {
        const day = usageRow.day.trim();
        const tokenAmount = normalizeBillingNonNegativeNumber(usageRow.tokenAmount);
        if (!day || tokenAmount <= 0) {
            continue;
        }

        const draft = getOrCreateDailyHistoryDraft(draftsByDay, day);
        const usageKey: ClientBillingOperationHistoryUsageKey = getClientBillingOperationHistoryUsageKey(
            usageRow.operation,
        );
        draft.usage[usageKey] = roundBillingTokenMath(draft.usage[usageKey] + tokenAmount);
    }

    let runningBalance = 0;
    const chronologicalRows = Array.from(draftsByDay.values())
        .sort((first, second) => first.day.localeCompare(second.day))
        .map((draft) => {
            const usedTokens = sumDailyHistoryUsage(draft.usage);
            const totalTokenDelta = roundBillingTokenMath(draft.topUpTokens - usedTokens);
            runningBalance = roundBillingTokenMath(runningBalance + totalTokenDelta);

            return {
                day: draft.day,
                balanceAtEndOfDay: runningBalance,
                totalTokenDelta,
                topUpTokens: draft.topUpTokens,
                usage: draft.usage,
            };
        });

    return chronologicalRows.reverse();
};
